<template>
  <div>
    <div class="mfg-header">
      <h4 class="mfg-title">Reports &amp; Reconciliation</h4>
    </div>

    <div class="filter-bar">
      <date-range-picker v-model="range" :auto-apply="true" :ranges="false" @update="loadAll" class="flex-grow-1">
        <template #input="picker">
          <span v-if="picker.startDate">{{ day(picker.startDate) }} – {{ day(picker.endDate) }}</span>
          <span v-else><i class="bi bi-calendar-event"></i> All dates</span>
        </template>
      </date-range-picker>
      <button class="btn btn-secondary" @click="clearRange">All dates</button>
    </div>

    <div class="mfg-tabs mb-4">
      <a v-for="t in tabs" :key="t.key" href="#" class="mfg-tab" :class="{ active: tab === t.key }" @click.prevent="tab = t.key">{{ t.label }}</a>
    </div>

    <div v-if="loading" class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary"></div></div>

    <!-- Reconciliation -->
    <div v-else-if="tab === 'reconciliation' && recon">
      <div class="row mb-4 pb-2 mfg-kpis">
        <div class="col-6 col-md-3 kpi-col">
          <div class="text-secondary small">Stock vs ledger</div>
          <div class="font-weight-bold" style="font-size:17px" :class="recon.stock.balanced ? 'text-primary' : 'text-danger'">{{ recon.stock.balanced ? 'Balanced' : recon.stock.mismatched.length + ' mismatched' }}</div>
        </div>
        <div class="col-6 col-md-3 kpi-col">
          <div class="text-secondary small">GL inventory</div>
          <div class="font-weight-bold" style="font-size:17px">{{ money(recon.valuation.gl_inventory_balance) }}</div>
        </div>
        <div class="col-6 col-md-3 kpi-col">
          <div class="text-secondary small">Ledger value (GL-posted movements)</div>
          <div class="font-weight-bold" style="font-size:17px">{{ money(recon.valuation.ledger_value_gl_posted_types) }}</div>
        </div>
        <div class="col-6 col-md-3 kpi-col">
          <div class="text-secondary small">Difference</div>
          <div class="font-weight-bold" style="font-size:17px" :class="Math.abs(recon.valuation.difference_gl_vs_posted_ledger) > 1 ? 'text-danger' : 'text-primary'">{{ money(recon.valuation.difference_gl_vs_posted_ledger) }}</div>
        </div>
      </div>

      <div v-if="Object.keys(recon.valuation.not_posted_to_gl || {}).length" class="alert alert-secondary small">
        <strong>Stock movements with no GL entry:</strong>
        <span v-for="(v, k) in recon.valuation.not_posted_to_gl" :key="k" class="mr-3">{{ k.replace(/_/g, ' ') }} {{ money(v.value) }} ({{ v.rows }})</span>
        <div class="mt-1">{{ recon.valuation.notes[0] }}</div>
      </div>
      <div v-if="recon.stock.unclassified_rows || recon.stock.unallocated_lot_rows" class="alert alert-secondary small">
        {{ recon.stock.unclassified_rows }} unclassified ledger row(s); {{ recon.stock.unallocated_lot_rows }} lot-tracked movement(s) without a lot.
      </div>

      <h6 class="section-title">Products out of balance</h6>
      <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
        <thead><tr><th>Product</th><th class="text-right">On hand</th><th class="text-right">Ledger</th><th class="text-right">Lots</th></tr></thead>
        <tbody>
          <tr v-for="r in recon.stock.mismatched" :key="r.product_id"><td>{{ r.product }}</td><td class="text-right">{{ qty(r.stored) }}</td><td class="text-right">{{ qty(r.ledger) }}</td><td class="text-right">{{ r.lots == null ? '—' : qty(r.lots) }}</td></tr>
          <tr v-if="!recon.stock.mismatched.length"><td colspan="4" class="text-muted">Every product's on-hand quantity equals its stock ledger{{ '' }} and lot balances.</td></tr>
        </tbody>
      </table></div>

      <h6 class="section-title">Production orders</h6>
      <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
        <thead><tr><th>Order</th><th>Product</th><th class="text-right">Materials (order / ledger)</th><th class="text-right">FG value (order / ledger)</th><th>Journal</th></tr></thead>
        <tbody>
          <tr v-for="o in recon.production.orders" :key="o.order_number">
            <td>{{ o.order_number }}</td><td>{{ o.output_product }}</td>
            <td class="text-right">{{ money(o.material_cost_order) }} / {{ money(o.material_cost_ledger) }}</td>
            <td class="text-right">{{ money(o.fg_value_order) }} / {{ money(o.fg_value_ledger) }}</td>
            <td><span class="badge" :class="o.reconciles ? 'badge-primary' : 'badge-danger'">{{ o.reconciles ? 'Reconciles' : 'Check' }}</span></td>
          </tr>
          <tr v-if="!recon.production.orders.length"><td colspan="5" class="text-muted">No completed production in this period.</td></tr>
        </tbody>
      </table></div>
    </div>

    <!-- Production -->
    <div v-else-if="tab === 'production' && production">
      <h6 class="section-title">By finished product</h6>
      <div class="table-responsive">
        <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
          <thead><tr><th>Product</th><th class="text-right">Orders</th><th class="text-right">Planned</th><th class="text-right">Actual</th><th class="text-right">Yield vs plan</th><th class="text-right">Materials</th><th class="text-right">Conversion</th><th class="text-right">Abnormal loss</th><th class="text-right">Usage variance</th><th class="text-right">Unit cost</th></tr></thead>
          <tbody>
            <tr v-for="r in production.by_product" :key="r.product">
              <td>{{ r.product }}</td><td class="text-right">{{ r.orders }}</td><td class="text-right">{{ qty(r.planned_output) }}</td><td class="text-right">{{ qty(r.actual_output) }}</td>
              <td class="text-right">{{ r.yield_vs_plan_pct == null ? '—' : r.yield_vs_plan_pct + '%' }}</td><td class="text-right">{{ money(r.material_cost) }}</td><td class="text-right">{{ money(r.conversion_cost) }}</td>
              <td class="text-right" :class="r.abnormal_loss > 0 ? 'text-danger' : ''">{{ money(r.abnormal_loss) }}</td><td class="text-right" :class="r.material_usage_variance > 0 ? 'text-danger' : ''">{{ money(r.material_usage_variance) }}</td><td class="text-right">{{ money(r.avg_unit_cost) }}</td>
            </tr>
          </tbody>
        </table></div>
      </div>
      <h6 class="section-title">Material usage</h6>
      <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
        <thead><tr><th>Material</th><th class="text-right">Planned</th><th class="text-right">Actual</th><th class="text-right">Variance</th><th class="text-right">Cost</th></tr></thead>
        <tbody><tr v-for="m in production.materials" :key="m.product"><td>{{ m.product }}</td><td class="text-right">{{ qty(m.planned) }} {{ m.unit }}</td><td class="text-right">{{ qty(m.actual) }}</td><td class="text-right" :class="m.variance > 0 ? 'text-danger' : ''">{{ qty(m.variance) }}</td><td class="text-right">{{ money(m.cost) }}</td></tr></tbody>
      </table></div>
      <h6 class="section-title">Wastage</h6>
      <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
        <thead><tr><th>Product</th><th>Of</th><th>Type</th><th class="text-right">Qty</th><th class="text-right">Value</th></tr></thead>
        <tbody><tr v-for="(w, i) in production.wastage" :key="i"><td>{{ w.product }}</td><td>{{ w.waste_of }}</td><td><span class="badge" :class="w.wastage_type === 'abnormal' ? 'badge-danger' : 'badge-secondary'">{{ w.wastage_type }}</span></td><td class="text-right">{{ qty(w.qty) }} {{ w.unit }}</td><td class="text-right">{{ money(w.value) }}</td></tr></tbody>
      </table></div>
    </div>

    <!-- Valuation -->
    <div v-else-if="tab === 'valuation' && valuation">
      <div class="mb-3"><span class="text-secondary small">Total stock value at current cost</span><div class="font-weight-bold" style="font-size:17px">{{ money(valuation.total) }}</div>
        <div v-if="valuation.products_missing_cost" class="text-danger small">{{ valuation.products_missing_cost }} product(s) in stock have no cost recorded.</div></div>
      <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
        <thead><tr><th>Product</th><th class="text-right">Quantity</th><th class="text-right">Unit cost</th><th class="text-right">Value</th></tr></thead>
        <tbody><tr v-for="r in valuation.rows" :key="r.product_id" v-show="r.quantity"><td>{{ r.product }}</td><td class="text-right">{{ qty(r.quantity) }} {{ r.unit }}</td><td class="text-right" :class="r.missing_cost ? 'text-danger' : ''">{{ r.unit_cost == null ? 'No cost' : money(r.unit_cost) }}</td><td class="text-right">{{ money(r.value) }}</td></tr></tbody>
      </table></div>
    </div>

    <!-- Margin -->
    <div v-else-if="tab === 'margin' && margin">
      <div class="mfg-box table-responsive mb-3"><table class="table table-sm mb-0">
        <thead><tr><th>Finished product</th><th class="text-right">Qty sold</th><th class="text-right">Revenue</th><th class="text-right">Cost</th><th class="text-right">Margin</th><th class="text-right">Margin %</th></tr></thead>
        <tbody><tr v-for="r in margin" :key="r.product"><td>{{ r.product }}</td><td class="text-right">{{ qty(r.qty_sold) }}</td><td class="text-right">{{ money(r.revenue) }}</td><td class="text-right">{{ money(r.cogs) }}</td><td class="text-right" :class="r.margin < 0 ? 'text-danger' : ''">{{ money(r.margin) }}</td><td class="text-right">{{ r.margin_pct == null ? '—' : r.margin_pct + '%' }}</td></tr></tbody>
      </table></div>
      <p class="small text-muted">Revenue from POS sales and dispatched invoice lines (net of tax); cost from the stock-ledger rows that removed the goods, at the finished-goods cost at the time.</p>
    </div>
  </div>
</template>

<script>
import { fmtMoney, fmtQty } from './mfgHelpers';

export default {
  name: 'ManufacturingReports',
  data() {
    return {
      tab: 'reconciliation',
      tabs: [
        { key: 'reconciliation', label: 'Reconciliation' },
        { key: 'production', label: 'Production & yield' },
        { key: 'valuation', label: 'Stock valuation' },
        { key: 'margin', label: 'Margins' },
      ],
      range: { startDate: null, endDate: null },
      loading: false, recon: null, production: null, valuation: null, margin: null,
    };
  },
  mounted() { this.loadAll(); },
  methods: {
    qty: fmtQty, money: fmtMoney,
    day(d) { return d ? new Date(d).toISOString().substring(0, 10) : ''; },
    params() { return this.range.startDate ? { from: this.day(this.range.startDate), to: this.day(this.range.endDate) } : {}; },
    clearRange() { this.range = { startDate: null, endDate: null }; this.loadAll(); },
    async loadAll() {
      this.loading = true;
      try {
        const p = { params: this.params() };
        const [r, pr, v, m] = await Promise.all([
          axios.get('/api/manufacturing/reports/reconciliation', p),
          axios.get('/api/manufacturing/reports/production', p),
          axios.get('/api/manufacturing/reports/valuation'),
          axios.get('/api/manufacturing/reports/margin', p),
        ]);
        this.recon = r.data; this.production = pr.data; this.valuation = v.data; this.margin = m.data;
      } finally { this.loading = false; }
    },
  },
};
</script>
